import {useTranslation} from "react-i18next";
import {useEffect, useState, useRef} from "react";
import NavBreadcrum from "components/NavBreadcrum";
import ButtonsActions from "views/utils/buttonsActions";
import TopBarActions from "components/TopBarActions";
import {SwalAlertChoice, SwalAlertClass} from "core/swalClass";
import axiApi from "ApiCaller";
import navs from "./navs";
import {ProfilesModel, ProfilesDateModel} from "site-apps/authuser/ProfilesModel";
import TableUsers from "./users/TableUsers";
import FormUser from "./users/FormUser";
import PrevDatesUser from "./users/PrevDatesUser";
import useCustomTranslation from "core/customTranslation";
import translations from "./users/translations";
import LoaderSpinner from "context/LoaderSpinner";
import FilterModel from "components/FilterModel";
import TopBarPagination from "components/paginator/TopBarPagination-v2";
import {PagerModel} from "components/paginator/PaginationModel";
import OrderingModel from "components/ordering/OrderingModel";
import ItemsModel from "core/itemsModel";

const buttonsActions = new ButtonsActions();

const ViewUsers = () => {
    const {t: _Tr} = useTranslation("routes");
    const {t: _Tf} = useTranslation("forms");
    const {t: _Tt} = useTranslation("tables");
    const {t: _Tc} = useCustomTranslation(translations);

    document.title = _Tr("labels.users");
    const bread = [
        {name: _Tr("labels.administration"), url: "/administration/", current: false},
        {name: document.title, current: true},
    ];

    const AlertQuestion = new SwalAlertChoice({
        icon: "info",
        iconColor: "#DD6B55",
        confirmButtonColor: "#DD6B55",
        confirmButtonText: _Tf("labels.delete"),
        cancelButtonText: _Tf("labels.cancel"),
    });

    const Alert = new SwalAlertClass({confirmButtonText: _Tc("buttons.accept")});

    const items = useRef(new ItemsModel());
    const filters = useRef(new FilterModel());
    const pagerModelRef = useRef(new PagerModel());
    const orderingRef = useRef(new OrderingModel());

    const [isLoading, setIsLoading] = useState(false);
    const [item, setItem] = useState({...ProfilesModel});
    const [panels, setPanels] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [dates, setDates] = useState({...ProfilesDateModel});
    const [checkedAll, setCheckedAll] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [buttonsTB, setButtonsTB] = useState({
        ...new ButtonsActions(),
        title: document.title,
    });
    // data table
    const [itemsShown, setItemsShown] = useState([]);

    const headersTable = [
        {name: _Tt("common.active"), attr: 0, direction: "asc", active: false},
        {name: _Tt("common.email"), attr: 1, direction: "asc", active: true, default: true},
        {name: _Tt("common.name"), attr: 2, direction: "asc", active: false},
        {name: _Tt("common.organizations"), attr: 3, direction: "asc", active: false},
        {name: _Tt("common.municipalities"), attr: 4, direction: "asc", active: false},
        {name: _Tt("users.is_admin"), attr: 5, direction: "asc", active: false},
        {name: _Tt("users.is_tech"), attr: 6, direction: "asc", active: false},
        {name: _Tt("users.is_maint"), attr: 7, direction: "asc", active: false},
        {name: _Tt("users.is_driver"), attr: 8, direction: "asc", active: false},
    ];

    useEffect(() => {
        const buttons = {...buttonsTB};
        buttons.title = document.title;
        buttons.edit.active = true;
        buttons.edit.onClick = onClickEdit;
        buttons.add.active = true;
        buttons.add.onClick = onClickAdd;
        buttons.add.disabled = false;
        buttons.delete.active = true;
        buttons.delete.onClick = onClickDelete;
        // -
        setButtonsTB(buttons);
        // -
        pagerModelRef.current.handleCallApi = () => fetchPostData();
        orderingRef.current.setHeaders(headersTable);
        orderingRef.current.applyOrder = () => fetchPostData();
        items.current.setItem = setItem;
        items.current.setShowPrev = setShowPreview;
        items.current.item = ProfilesModel;

        filters.current.filter = (e) => {
            pagerModelRef.current.paging.page = 1;
            fetchPostData();
        };

        fetchGetData();
    }, []);

    const fetchGetData = () => {
        setIsLoading(true);
        pagerModelRef.current.active = false;
        axiApi
            .get("/api/profiles/")
            .then((resp) => {
                if (resp.data) {
                    pagerModelRef.current.active = resp.data.items.length > 0;
                    items.current.showed = [...resp.data.items];
                    setItemsShown([...resp.data.items]);
                    setPanels(resp.data.panels);
                    setOrganizations(resp.data.organizations);
                    const buttons = {...buttonsTB};
                    buttons.title = document.title;
                    setButtonsTB(buttons);
                    pagerModelRef.current.paging.setPager(resp.data);
                } else {
                    Alert.warning(_Tc("alerts.error"), _Tc("alerts.serverError"));
                }
            })
            .catch((error) => {
                console.error(error);
                Alert.warning(_Tc("alerts.error"), _Tc("alerts.serverError"));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchPostData = () => {
        setIsLoading(true);
        pagerModelRef.current.active = false;
        axiApi
            .post("/api/profiles/", filters.current.chosen, {
                params: {...pagerModelRef.current.paging.getParams(), ...orderingRef.current.getParams()},
            })
            .then((resp) => {
                if (resp.data) {
                    pagerModelRef.current.active = resp.data.items.length > 0;
                    items.current.setShowed(resp.data.items);
                    setItemsShown([...items.current.showed]);
                    pagerModelRef.current.paging.setPager(resp.data);
                } else {
                    Alert.warning(_Tc("alerts.error"), _Tc("alerts.serverError"));
                }
            })
            .catch((error) => {
                console.error(error);
                Alert.warning(_Tc("alerts.error"), _Tc("alerts.serverError"));
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const onClickEdit = () => {
        if (items.current.chosen.length === 1) {
            let choose = items.current.showed.find((item) => item.id === items.current.chosen[0]);
            setItem(choose);
            setShowForm(true);
        }
    };

    const onClickAdd = () => {
        setItem({...ProfilesModel});
        setShowForm(true);
    };

    const onClickDelete = () => {
        let users = items.current.showed.filter((item) => items.current.chosen.includes(item.id)).map((item) => item.token);

        if (!users.length) {
            Alert.warning(_Tc("alerts.error"), _Tc("alerts.noUserSelected"));
            return false;
        }

        AlertQuestion.fire(_Tc("Confirme el borrado"), _Tc("De los usuarios seleccionados")).then((quest) => {
            if (quest.isConfirmed) {
                axiApi
                    .post("/api/profiles/delete/", {users: users})
                    .then((r) => {
                        if (r.data.status) {
                            fetchGetData();
                        } else {
                            Alert.warning(_Tc("alerts.error"), r.data.message);
                        }
                    })
                    .catch((r) => {
                        console.error(r);
                        Alert.error(_Tc("alerts.error"), _Tc("alerts.serverError"));
                    });
            }
        });
    };

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <TopBarActions actions={buttonsTB} filtersRef={filters} />
            <TopBarPagination pagerModel={pagerModelRef.current} stickyTopValue="125px" chosen={items.current.chosen} />
            <div className="row">
                <div className="col">
                    <LoaderSpinner isLoading={isLoading}>
                        <TableUsers
                            itemsShow={itemsShown}
                            items={items}
                            checkAll={checkedAll}
                            setCheckAll={setCheckedAll}
                            buttonsTB={buttonsTB}
                            setButtonsTB={setButtonsTB}
                            orderingRef={orderingRef}
                        />
                    </LoaderSpinner>
                </div>
                <PrevDatesUser item={item} showPreview={showPreview} />
            </div>
            <FormUser
                setShowCanvas={setShowForm}
                showCanvas={showForm}
                item={item}
                items={items}
                setItem={setItem}
                setItemsShown={setItemsShown}
                organizations={organizations}
                panels={panels}
                reloadData={fetchGetData}
            />
        </>
    );
};

export default ViewUsers;
